import Env from '/env'

let ws
let reconnectAttempts = 0
let reconnectTimer = null
let heartbeatTimer = null
let pongTimer = null
let manuallyClosed = false
const subscriptions = new Set() // храним все активные каналы (токен читаем свежий при send)
const messageHandlers = new Set() // несколько подписчиков на сообщения

// Хост сокета — тот же, что у API_URL (test: backendtest.bigplay.gg, prod: core.bigplay.gg),
// путь на обоих стендах одинаковый. Хардкодить адрес нельзя — прод-сборка уедет на тестовый бэк.
const WS_PATH = '/chat-test'

function resolveWsUrl() {
    if (Env.WS_URL) return Env.WS_URL

    const api = String(Env.API_URL || '').replace(/\/+$/, '')
    if (!api) return `wss://backendtest.bigplay.gg${WS_PATH}`

    return `${api.replace(/^http/, 'ws')}${WS_PATH}`
}

const WS_URL = resolveWsUrl()
const HEARTBEAT_INTERVAL_MS = 25000
const PONG_TIMEOUT_MS = 10000
const WAKE_PONG_TIMEOUT_MS = 3000
const MAX_RECONNECT_DELAY_MS = 8000

function getAuthToken() {
    return localStorage.getItem('token')
}

function clearReconnectTimer() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
    }
}

function clearPongTimer() {
    if (pongTimer) {
        clearTimeout(pongTimer)
        pongTimer = null
    }
}

function clearHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
    }
    clearPongTimer()
}

// Если в течение таймаута нет ни pong'а, ни любого другого сообщения — считаем соединение мёртвым
function armPongTimer(timeoutMs = PONG_TIMEOUT_MS) {
    clearPongTimer()
    pongTimer = setTimeout(() => {
        console.warn('WebSocket: pong timeout — форсируем реконнект')
        if (ws) {
            try {
                ws.close(4000, 'no-pong')
            } catch (e) {
                console.error('WebSocket force-close failed:', e)
            }
        }
    }, timeoutMs)
}

function sendPing(pongTimeoutMs = PONG_TIMEOUT_MS) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
        ws.send(JSON.stringify({cmd: 'ping'}))
    } catch (e) {
        console.error('WebSocket ping failed:', e)
        return
    }
    armPongTimer(pongTimeoutMs)
}

function startHeartbeat() {
    clearHeartbeat()
    heartbeatTimer = setInterval(() => sendPing(), HEARTBEAT_INTERVAL_MS)
}

function scheduleReconnect() {
    if (manuallyClosed) return
    if (reconnectTimer) return

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY_MS)
    reconnectAttempts += 1
    console.log(`WebSocket: reconnect scheduled in ${delay}ms (attempt ${reconnectAttempts})`)

    reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connectToWs()
    }, delay)
}

function sendSubscribe(channel) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
        ws.send(JSON.stringify({
            cmd: 'subscribe',
            channel,
            data: {token: getAuthToken()}
        }))
    } catch (e) {
        console.error('WebSocket subscribe failed:', e)
    }
}

export function connectToWs() {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        return ws
    }

    manuallyClosed = false
    ws = new WebSocket(WS_URL)

    ws.onopen = () => {
        console.log('WebSocket connected:', WS_URL)
        reconnectAttempts = 0
        clearReconnectTimer()
        startHeartbeat()

        // Восстанавливаем/устанавливаем все подписки со свежим токеном
        subscriptions.forEach((channel) => sendSubscribe(channel))
    }

    ws.onerror = (error) => {
        console.error('WebSocket error:', error)
    }

    ws.onclose = (event) => {
        console.warn(`WebSocket closed (code=${event.code}, reason=${event.reason || 'n/a'})`)
        clearHeartbeat()
        ws = null
        if (!manuallyClosed) {
            scheduleReconnect()
        }
    }

    ws.onmessage = (event) => {
        // Любое входящее сообщение — сигнал, что соединение живое
        clearPongTimer()

        let data
        try {
            data = JSON.parse(event.data)
        } catch (e) {
            console.error('Error parsing WebSocket message:', e)
            return
        }

        // Служебные ответы наверх не пробрасываем
        if (data && (data.event === 'pong' || data.type === 'pong' || data.cmd === 'pong')) {
            return
        }

        // Ошибки подписки/авторизации — форсим реконнект, чтобы re-subscribe пошёл со свежим токеном
        if (data && (data.event === 'subscription_failed'
                  || data.event === 'auth_error'
                  || data.type === 'error')) {
            console.error('WebSocket server error:', data)
            if (ws) {
                try {
                    ws.close(4001, 'server-error')
                } catch (e) {
                    console.error('WebSocket force-close failed:', e)
                }
            }
            return
        }

        messageHandlers.forEach(handler => {
            try {
                handler(data)
            } catch (err) {
                console.error('WebSocket handler threw:', err)
            }
        })
    }
    return ws
}

// Токен из аргумента игнорируется — читаем свежий из localStorage в момент отправки,
// чтобы после реконнекта не улетал протухший токен.
// eslint-disable-next-line no-unused-vars
export function subscribe(channel, _token) {
    subscriptions.add(channel)
    if (ws && ws.readyState === WebSocket.OPEN) {
        sendSubscribe(channel)
    } else {
        // Соединения нет — инициируем; onopen сам восстановит подписку из subscriptions
        connectToWs()
    }
}

export function unsubscribe(channel) {
    subscriptions.delete(channel)

    if (ws && ws.readyState === WebSocket.OPEN) {
        try {
            ws.send(JSON.stringify({
                cmd: 'unsubscribe',
                channel
            }))
        } catch (e) {
            console.error('WebSocket unsubscribe failed:', e)
        }
    }
}

export function sendMessage(data) {
    if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data))
    }
}

// Регистрирует обработчик входящих сообщений. Возвращает функцию отписки.
// Несколько обработчиков могут быть зарегистрированы одновременно —
// каждый получит все сообщения и должен сам фильтровать по channel/event.
export function onMessage(callback) {
    messageHandlers.add(callback)
    return () => messageHandlers.delete(callback)
}

export function closeWs() {
    manuallyClosed = true
    clearReconnectTimer()
    clearHeartbeat()
    subscriptions.clear()
    messageHandlers.clear()
    if (ws) {
        ws.close()
        ws = null
    }
    reconnectAttempts = 0
}

// Проверка «живости» после возврата вкладки/сети.
// Если сокет якобы OPEN, но серверу уже отвалились — увидим по pong-таймауту.
function verifyLiveness() {
    if (manuallyClosed) return
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        clearReconnectTimer()
        reconnectAttempts = 0
        connectToWs()
        return
    }
    if (ws.readyState === WebSocket.OPEN) {
        sendPing(WAKE_PONG_TIMEOUT_MS)
    }
}

// Ускоряем восстановление после «сна» вкладки/потери сети
if (typeof window !== 'undefined') {
    window.addEventListener('online', verifyLiveness)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') verifyLiveness()
    })
}
